import React, { FC, useEffect, useRef } from 'react';

import { alpha, styled } from '@mui/material/styles';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import TestCard, { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import {
  Info,
  InfoRow,
  ItemWrapper,
  OrderLabel,
  StyledTitle,
  StyledReorderIcon,
} from '~/components/Test/OrderingQuestion/styles';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { SMALL_MARGIN_PX, useIsWidthUp } from '~/theme';

export const QUIZ_ORDER_ANSWER = 'QuizOrderAnswer';

interface OrderingQuestionAnswerProps {
  id?: string | null;
  title?: string | null;
  index: number;
  orderIndex: number | null;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  handleDrop: (id: string, index: number) => void;
  isSmall?: boolean;
  disabled?: boolean;
  displayResults?: boolean;
  isCorrect?: boolean;
  colorsMode?: QuizColorsMode;
}

const OrderingQuestionAnswer: FC<OrderingQuestionAnswerProps> = ({
  id,
  title,
  index,
  orderIndex,
  moveCard,
  handleDrop,
  isSmall,
  disabled,
  displayResults,
  isCorrect,
  colorsMode,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTenantTranslation();
  const isMobile = !useIsWidthUp('md');

  const [{ handlerId }, drop] = useDrop(
    {
      accept: QUIZ_ORDER_ANSWER,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item, monitor) {
        if (!ref.current) {
          return;
        }
        // @ts-ignore
        const dragIndex = item?.index;
        const hoverIndex = index;
        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }
        // Determine rectangle on screen
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // Determine mouse position
        const clientOffset = monitor.getClientOffset();
        // Get pixels to the top
        // @ts-ignore
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%
        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }
        // Time to actually perform the action
        moveCard(dragIndex, hoverIndex);
        // @ts-ignore
        item.index = hoverIndex;
      },
      drop(item) {
        // @ts-ignore
        handleDrop(item?.id, item?.index);
      },
    },
    [index, moveCard],
  );
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: QUIZ_ORDER_ANSWER,
      item: { id, index, title },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => !disabled,
    }),
    [id, index, title, disabled],
  );

  drag(drop(ref));

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <>
      <StyledItemWrapper
        ref={ref}
        isDragging={isDragging}
        data-handler-id={handlerId}
        disabled={disabled}
        isWrong={!isCorrect}
        displayResults={displayResults}
        data-testid="quizQuestionAnswer"
        colorsMode={colorsMode}
      >
        <InfoRow>
          <Info>
            <StyledReorderIcon className="reorderIcon" />
            <StyledTitle data-testid="quizQuestionAnswerTitle" isSmall={isSmall}>
              {title}
            </StyledTitle>
          </Info>
          <Info>
            <OrderLabel data-testid="quizQuestionAnswerOrder" className="orderLabel">
              {typeof orderIndex === 'number'
                ? t('common.orderKey', { count: index + 1, ordinal: true })
                : ''}
            </OrderLabel>
            {!isMobile && displayResults && (
              <div>{isCorrect ? <StyledCorrectBadge /> : <StyledIncorrectBadge />}</div>
            )}
          </Info>
        </InfoRow>
        {isMobile && displayResults && (
          <MobileBadgeRow>
            {isCorrect ? <MobileCorrectBadge /> : <MobileIncorrectBadge />}
          </MobileBadgeRow>
        )}
      </StyledItemWrapper>
    </>
  );
};

export default OrderingQuestionAnswer;

const StyledItemWrapper = styled(ItemWrapper, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<{ isDragging?: boolean }>`
  opacity: ${({ isDragging }) => (isDragging ? 0 : 1)};

  &:hover {
    & .orderLabel {
      ${({ disabled, theme }) =>
        !disabled && 'background: ' + alpha(theme.palette.common.white, 0.05) + ';'}
    }
  }
`;
const StyledCorrectBadge = styled(TestCard.CorrectBadge)`
  margin-left: ${SMALL_MARGIN_PX};
`;
const StyledIncorrectBadge = styled(TestCard.IncorrectBadge)`
  margin-left: ${SMALL_MARGIN_PX};
`;
const MobileBadgeRow = styled('div')`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: ${SMALL_MARGIN_PX};
`;
const createMobileBadgeStyles = (component) => styled(component)`
  display: flex;
  align-items: center;

  & span {
    font-size: 12px;
    line-height: 1;
  }
`;
const MobileCorrectBadge = createMobileBadgeStyles(TestCard.CorrectBadge);
const MobileIncorrectBadge = createMobileBadgeStyles(TestCard.IncorrectBadge);
